import { call, select, take } from 'redux-saga/effects';
import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import _camelCase from 'lodash/camelCase';
import _identity from 'lodash/identity';
import _includes from 'lodash/includes';
import _isArray from 'lodash/isArray';
import _isPlainObject from 'lodash/isPlainObject';
import _mapKeys from 'lodash/mapKeys';
import _mapValues from 'lodash/mapValues';
import _snakeCase from 'lodash/snakeCase';
import { MSI_SOURCES, VENUE_SOURCE } from 'constants/logic';
import { countryCodeToCurrencyCode } from 'constants/countryCodeToStaticValueMaps';
import { repairVenueAliasUrl } from 'helpers/consumer-urls';

export const isRegexMatch = (searchText, pattern) => searchText.match(pattern);

export function* fetchEntityIfNotLoading(entity, dependencies) {
  const isLoading = yield select(entity.loading);

  if (!isLoading) {
    return yield call(fetchEntityIfNeeded, entity, dependencies);
  }

  const { data } = yield take(action =>
    _includes([entity.successAction().type, entity.errorAction().type], action.type)
  );
  return data;
}

export const userHasPermission = (user, perm) => (user && user.permissions ? user.permissions.includes(perm) : false);

export const transformKeys = (object, predicate = _identity, preserveKeys = []) => {
  if (_isArray(object)) {
    return object.map(value => transformKeys(value, predicate, preserveKeys));
  }

  if (_isPlainObject(object)) {
    return _mapValues(
      _mapKeys(object, (value, key) => (preserveKeys.includes(key) ? _identity(key) : predicate(key))),
      value => transformKeys(value, predicate, preserveKeys)
    );
  }

  return object;
};

export const transformValues = (obj, pred = _identity) => {
  if (_isArray(obj)) {
    return obj.map(v => transformValues(v, pred));
  }
  if (_isPlainObject(obj)) {
    return _mapValues(obj, v => transformValues(v, pred));
  }
  if (obj === undefined) {
    return obj;
  }
  return pred(obj);
};

export const userHasRole = (user, role) => (user && user.role ? user.role === role : false);

export const transformKeysToCamelCase = (object, preserveKeys = []) => transformKeys(object, _camelCase, preserveKeys);
export const transformKeysToSnakeCase = (object, preserveKeys = []) => transformKeys(object, _snakeCase, preserveKeys);
export const renameKeys = (obj, keyMap) => transformKeys(obj, key => keyMap[key] || key);

export const transformClientResponse = response => {
  const data = response instanceof Error ? JSON.parse(response.response.data.data) : response.data.data;
  return transformKeysToCamelCase(data);
};
export const transformV2ClientResponse = res => transformKeysToCamelCase(res.data);

export const parseNumber = value => {
  const parsed = parseInt(value, 10);
  // FYI: class methods differ from their global equivalent
  //   isFinite(NaN) === true
  //   Number.isFinite(NaN) === false
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatUserInfo = (rawUser = {}) => {
  const user = transformKeysToCamelCase(rawUser);
  const { venuesList = [] } = user;

  return {
    ...user,
    venuesList: venuesList.map(venue => {
      const { countryCode, venueSource, venueAliasUrl } = venue;
      const isMsInventoryVenue = MSI_SOURCES.includes(venueSource);
      return {
        ...venue,
        venueAliasUrl: repairVenueAliasUrl(venueAliasUrl),
        currencyCode: countryCodeToCurrencyCode[countryCode],
        isMsInventoryVenue,
        isReservationsStoppable: venueSource === VENUE_SOURCE.CLASSTIVITY,
      };
    }),
  };
};

export const trimTimezone = dateString => dateString && dateString.substring(0, 19);

// a simplification of the already-simple "Fuzzy" (https://github.com/myork/fuzzy)
export function isFuzzyMatch(pattern = '', str = '') {
  const compareString = str.toLowerCase();
  const comparePattern = pattern.toLowerCase();
  let patternIdx = 0;

  if (compareString === comparePattern) return true;

  for (let idx = 0; idx < str.length; idx += 1) {
    if (compareString[idx] === comparePattern[patternIdx]) {
      patternIdx += 1;
    }
  }

  return patternIdx === pattern.length;
}

export const displayName = child => child.type && (child.type.displayName || child.type.name);
// borrowed from https://github.com/facebook/react/tree/master/packages/react-is
const symbolFor = str => {
  if (typeof Symbol === 'function' && Symbol.for) {
    return Symbol.for(str);
  }

  const dict = {
    'react.element': 0xeac7,
    'react.fragment': 0xeacb,
  };
  return dict[str];
};
export const isReactElement = child =>
  child !== null && typeof child === 'object' && child.$$typeof === symbolFor('react.element');
export const isNativeElement = child => isReactElement(child) && typeof child.type === 'string';
export const isFragment = child => isReactElement(child) && child.type === symbolFor('react.fragment');
